import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getPoint } from './utils/getPoint';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatSelector } from '../../selectors';
import { TPosition } from '../../types/messageContextMenu';

const DEFAULT_MOVE_THRESHOLD = 10;
const DEFAULT_LONG_PRESS_DELAY = 750;
const LONG_PRESS_ATTR = 'data-long-press';

interface IUseMobileContextMenuParams {
  onShowContextMenu: (position?: TPosition) => void;
  elementRef: React.RefObject<HTMLElement | null>;
  isMenuEnabled: boolean;
}

export const useMobileContextMenu = ({ onShowContextMenu, elementRef, isMenuEnabled }: IUseMobileContextMenuParams) => {
  const [isLongPressTriggered, setIsLongPressTriggered] = useState(false);
  const [isLongPressActive, setIsLongPressActive] = useState(false);

  const timeout = useRef<number | null>(null);
  const startPoint = useRef<{ x: number; y: number } | null>(null);
  const currentChat = useSelector(currentOpenChatSelector);

  const effectiveDelay = currentChat?.chatId === CHATBOT_ID ? DEFAULT_LONG_PRESS_DELAY * 2 : DEFAULT_LONG_PRESS_DELAY;

  const handleTouchStart = useCallback(
    (event: Event) => {
      setIsLongPressActive(true);
      startPoint.current = getPoint(event);
      timeout.current = window.setTimeout(() => {
        onShowContextMenu();
        setIsLongPressTriggered(true);
        setIsLongPressActive(false);
      }, effectiveDelay);
    },
    [onShowContextMenu, effectiveDelay],
  );

  const handleTouchEnd = useCallback(
    (event: Event) => {
      setIsLongPressActive(false);
      startPoint.current = null;
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      if (event.target && isLongPressTriggered) {
        event.preventDefault();
      }

      setIsLongPressTriggered(false);
    },
    [isLongPressTriggered],
  );

  const handleTouchCancel = useCallback(() => {
    setIsLongPressActive(false);
    startPoint.current = null;
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    setIsLongPressTriggered(false);
  }, []);

  const handleTouchMove = useCallback(
    (event: Event) => {
      if (!startPoint.current) {
        return;
      }

      const point = getPoint(event);

      if (!point) {
        return;
      }

      const isMovedBeyondThreshold =
        Math.abs(point.x - startPoint.current.x) > DEFAULT_MOVE_THRESHOLD ||
        Math.abs(point.y - startPoint.current.y) > DEFAULT_MOVE_THRESHOLD;

      if (isMovedBeyondThreshold) {
        handleTouchCancel();
      }
    },
    [handleTouchCancel],
  );

  const pressedElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || !isMenuEnabled) {
      return;
    }

    if (isLongPressActive) {
      pressedElementRef.current = element;
      element.setAttribute(LONG_PRESS_ATTR, '');
    } else if (pressedElementRef.current) {
      pressedElementRef.current.removeAttribute(LONG_PRESS_ATTR);
      pressedElementRef.current = null;
    }
  }, [isLongPressActive, elementRef, isMenuEnabled]);

  useEffect(() => {
    const element = elementRef.current;

    if (!element || !isMenuEnabled) {
      return () => {};
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchCancel);
    element.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchCancel);
      element.removeEventListener('touchmove', handleTouchMove);
    };
  }, [handleTouchStart, handleTouchEnd, handleTouchCancel, handleTouchMove, elementRef, isMenuEnabled]);

  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, []);
};
